/**
 * Distance + ETA formatters used across the entire app.
 *
 * Backend stores routes in SI units (`distance_km`, `duration_min`) because
 * that is what OSRM returns. The product is US-only and the operator wanted
 * to see everything in **miles** and **hours**, plus a flat **2-minute
 * per-stop dwell time** added on top of OSRM's drive duration to get a more
 * realistic ETA.
 *
 * Keep this file as the single source of truth — every display surface
 * (Driver app, Dispatcher, Reports, toasts, PDFs-via-API) imports from here.
 */

export const KM_PER_MILE = 1.609344;
export const STOP_DWELL_MIN = 2; // minutes added per stop to OSRM duration

export function kmToMiles(km) {
  const n = Number(km);
  if (!isFinite(n)) return 0;
  return n / KM_PER_MILE;
}

/** "13.7 mi" — 1 decimal place; always falls back to "0.0 mi". */
export function fmtDistance(km, { suffix = "mi" } = {}) {
  return `${kmToMiles(km).toFixed(1)} ${suffix}`;
}

/**
 * Travel ETA in hours, including a 2-min-per-stop dwell.
 * Returns a compact string:
 *   - 0 → "0 min"
 *   - < 1 h → "37 min"
 *   - integer hours → "2 h"
 *   - fractional → "1 h 25 min"
 */
export function fmtEta(durationMin, stopsCount = 0) {
  const total = Math.max(0, Number(durationMin) || 0) + Math.max(0, Number(stopsCount) || 0) * STOP_DWELL_MIN;
  if (total < 1) return "0 min";
  if (total < 60) return `${Math.round(total)} min`;
  const h = Math.floor(total / 60);
  const m = Math.round(total - h * 60);
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

/** Same numbers used by `fmtEta` but returned in hours as a decimal — handy
 *  for places that want to show "≈ 1.4 h" instead of a split label. */
export function etaHours(durationMin, stopsCount = 0) {
  const total = (Number(durationMin) || 0) + (Number(stopsCount) || 0) * STOP_DWELL_MIN;
  return total / 60;
}
